import { useState } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import {
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Upload,
  FileText,
  User,
  Globe,
  Calendar,
  AlertCircle,
  Loader2,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";

/* ── Country / Visa data ─────────────────────────────── */
const COUNTRIES = [
  {
    id: "usa",
    name: "АНУ 🇺🇸",
    flag: "🇺🇸",
    visaTypes: [
      { id: "b1b2", label: "B1/B2 Аялал / Бизнес", purpose: "travel", fee: "450,000₮" },
      { id: "f1", label: "F-1 Оюутны виз", purpose: "study", fee: "650,000₮" },
    ],
  },
  {
    id: "australia",
    name: "Австрали 🇦🇺",
    flag: "🇦🇺",
    visaTypes: [
      { id: "visitor", label: "Visitor Visa", purpose: "travel", fee: "380,000₮" },
      { id: "student", label: "Student Visa (500)", purpose: "study", fee: "580,000₮" },
      { id: "work", label: "Work Visa (482)", purpose: "work", fee: "680,000₮" },
    ],
  },
  {
    id: "canada",
    name: "Канад 🇨🇦",
    flag: "🇨🇦",
    visaTypes: [
      { id: "tourist", label: "Tourist Visa", purpose: "travel", fee: "350,000₮" },
      { id: "express", label: "Express Entry", purpose: "work", fee: "850,000₮" },
    ],
  },
  {
    id: "czech",
    name: "Чех 🇨🇿",
    flag: "🇨🇿",
    visaTypes: [
      { id: "work_short", label: "Ажлын виз (богино)", purpose: "work", fee: "420,000₮" },
      { id: "work_long", label: "Ажлын виз (урт)", purpose: "work", fee: "620,000₮" },
    ],
  },
  {
    id: "ireland",
    name: "Ирланд 🇮🇪",
    flag: "🇮🇪",
    visaTypes: [
      { id: "language", label: "Хэлний сургууль", purpose: "study", fee: "480,000₮" },
      { id: "work_ie", label: "Ажлын виз", purpose: "work", fee: "580,000₮" },
    ],
  },
];

const DOCS_BY_PURPOSE: Record<string, { type: string; label: string; required: boolean }[]> = {
  travel: [
    { type: "passport", label: "Паспорт (скан)", required: true },
    { type: "photo", label: "Зураг (3.5×4.5 см)", required: true },
    { type: "bank_statement", label: "Банкны хуулга (3 сар)", required: true },
    { type: "employment", label: "Ажлын тодорхойлолт", required: true },
    { type: "itinerary", label: "Аялалын маршрут", required: false },
  ],
  study: [
    { type: "passport", label: "Паспорт (скан)", required: true },
    { type: "photo", label: "Зураг (3.5×4.5 см)", required: true },
    { type: "admission", label: "Элсэлтийн баримт", required: true },
    { type: "bank_statement", label: "Банкны хуулга (6 сар)", required: true },
    { type: "diploma", label: "Боловсролын гэрчилгээ", required: true },
    { type: "ielts", label: "IELTS / TOEFL оноо", required: false },
  ],
  work: [
    { type: "passport", label: "Паспорт (скан)", required: true },
    { type: "photo", label: "Зураг (3.5×4.5 см)", required: true },
    { type: "cv", label: "CV / Анкет", required: true },
    { type: "diploma", label: "Боловсролын гэрчилгээ", required: true },
    { type: "job_offer", label: "Ажлын урилга", required: true },
    { type: "bank_statement", label: "Банкны хуулга", required: false },
  ],
};

const STEPS = [
  { id: 1, label: "Улс & Виз", icon: Globe },
  { id: 2, label: "Хувийн мэдээлэл", icon: User },
  { id: 3, label: "Баримт бичиг", icon: FileText },
  { id: 4, label: "Баталгаажуулалт", icon: CheckCircle2 },
];

interface FormData {
  country: string;
  visaType: string;
  purpose: "travel" | "study" | "work";
  serviceFee: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  passportNumber: string;
  passportExpiry: string;
  nationality: string;
  phone: string;
  email: string;
  plannedDepartureDate: string;
  plannedReturnDate: string;
  travelPurposeDetail: string;
}

export default function Apply() {
  const [, navigate] = useLocation();
  const { user, isAuthenticated, loading } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    country: "",
    visaType: "",
    purpose: "travel",
    serviceFee: "",
    firstName: "",
    lastName: "",
    birthDate: "",
    passportNumber: "",
    passportExpiry: "",
    nationality: "Монгол",
    phone: "",
    email: "",
    plannedDepartureDate: "",
    plannedReturnDate: "",
    travelPurposeDetail: "",
  });
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, { url: string; name: string }>>({});
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
  const [applicationId, setApplicationId] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const createApplication = trpc.applications.create.useMutation();
  const uploadDoc = trpc.documents.upload.useMutation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A1628]">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A1628] text-white px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Нэвтрэх шаардлагатай</h2>
          <p className="text-gray-400 mb-6">
            Захиалга хийхийн тулд эхлээд бүртгэл үүсгэж нэвтрэнэ үү.
          </p>
          <a
            href={getLoginUrl()}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
          >
            Нэвтрэх / Бүртгүүлэх
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  const selectedCountry = COUNTRIES.find((c) => c.id === formData.country);
  const selectedVisa = selectedCountry?.visaTypes.find((v) => v.id === formData.visaType);
  const requiredDocs = DOCS_BY_PURPOSE[formData.purpose] || [];

  const handleDocUpload = async (docType: string, docLabel: string, file: File) => {
    if (!applicationId) return;
    setUploadingDoc(docType);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result as string;
        await uploadDoc.mutateAsync({
          applicationId,
          docType,
          docLabel,
          fileName: file.name,
          mimeType: file.type,
          base64,
        });
        setUploadedDocs((prev) => ({ ...prev, [docType]: { url: "#", name: file.name } }));
        setUploadingDoc(null);
      };
      reader.readAsDataURL(file);
    } catch {
      setUploadingDoc(null);
      setError("Файл upload хийхэд алдаа гарлаа");
    }
  };

  const handleSubmitApplication = async () => {
    setError("");
    try {
      const result = await createApplication.mutateAsync({
        country: selectedCountry?.name || formData.country,
        visaType: selectedVisa?.label || formData.visaType,
        purpose: formData.purpose,
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthDate: formData.birthDate,
        passportNumber: formData.passportNumber || undefined,
        passportExpiry: formData.passportExpiry || undefined,
        nationality: formData.nationality,
        phone: formData.phone,
        email: formData.email,
        plannedDepartureDate: formData.plannedDepartureDate || undefined,
        plannedReturnDate: formData.plannedReturnDate || undefined,
        serviceFee: selectedVisa?.fee,
      });
      setApplicationId(result.id);
      setStep(3);
    } catch (e: any) {
      setError(e.message || "Алдаа гарлаа");
    }
  };

  const handleFinish = () => {
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-[#0A1628] flex flex-col">
          <Header />
          <div className="flex-1 flex items-center justify-center px-4 py-20">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center max-w-lg"
            >
              <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-12 h-12 text-green-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">Захиалга амжилттай!</h2>
              <p className="text-gray-400 mb-2">
                Таны захиалгын дугаар: <span className="text-blue-400 font-bold">#{applicationId}</span>
              </p>
              <p className="text-gray-400 mb-8">
                Манай зөвлөх 24 цагийн дотор таны {formData.phone} дугаарт холбогдох болно.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                >
                  Захиалгаа харах
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="border border-white/20 text-white hover:bg-white/10 px-6 py-3 rounded-xl font-semibold transition-colors"
                >
                  Нүүр хуудас
                </button>
              </div>
            </motion.div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-[#0A1628] flex flex-col">
        <Header />

        <div className="flex-1 py-10 px-4">
          <div className="max-w-3xl mx-auto">
            {/* Page Title */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Визийн захиалга</h1>
              <p className="text-gray-400">Доорх алхмуудыг дагаж захиалгаа бөглөнө үү</p>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center justify-center mb-10 gap-0">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const isActive = step === s.id;
                const isDone = step > s.id;
                return (
                  <div key={s.id} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                          isDone
                            ? "bg-green-500 text-white"
                            : isActive
                            ? "bg-blue-600 text-white ring-4 ring-blue-600/30"
                            : "bg-white/10 text-gray-400"
                        }`}
                      >
                        {isDone ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                      </div>
                      <span
                        className={`text-xs mt-1 hidden sm:block ${
                          isActive ? "text-blue-400 font-semibold" : isDone ? "text-green-400" : "text-gray-500"
                        }`}
                      >
                        {s.label}
                      </span>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div
                        className={`w-12 sm:w-20 h-0.5 mx-1 ${
                          step > s.id ? "bg-green-500" : "bg-white/10"
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Form Card */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8">
              <AnimatePresence mode="wait">
                {/* STEP 1: Country & Visa */}
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h2 className="text-xl font-bold text-white mb-6">Улс болон визийн төрлөө сонгоно уу</h2>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                      {COUNTRIES.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => {
                            setFormData((p) => ({ ...p, country: c.id, visaType: "", purpose: "travel" }));
                          }}
                          className={`p-4 rounded-xl border text-left transition-all ${
                            formData.country === c.id
                              ? "border-blue-500 bg-blue-500/20 text-white"
                              : "border-white/10 bg-white/5 text-gray-300 hover:border-white/30"
                          }`}
                        >
                          <div className="text-2xl mb-1">{c.flag}</div>
                          <div className="font-semibold text-sm">{c.name.split(' ').slice(0, -1).join(' ') || c.name}</div>
                        </button>
                      ))}
                    </div>

                    {selectedCountry && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <h3 className="text-white font-semibold mb-3">Визийн төрөл</h3>
                        <div className="space-y-2 mb-6">
                          {selectedCountry.visaTypes.map((v) => (
                            <button
                              key={v.id}
                              onClick={() =>
                                setFormData((p) => ({
                                  ...p,
                                  visaType: v.id,
                                  purpose: v.purpose as "travel" | "study" | "work",
                                  serviceFee: v.fee,
                                }))
                              }
                              className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${
                                formData.visaType === v.id
                                  ? "border-blue-500 bg-blue-500/20 text-white"
                                  : "border-white/10 bg-white/5 text-gray-300 hover:border-white/30"
                              }`}
                            >
                              <span className="font-medium">{v.label}</span>
                              <span className="text-blue-400 font-bold text-sm">{v.fee}</span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    <div className="flex justify-end">
                      <button
                        disabled={!formData.country || !formData.visaType}
                        onClick={() => setStep(2)}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                      >
                        Дараагийн алхам <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* STEP 2: Personal Info */}
                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h2 className="text-xl font-bold text-white mb-6">Хувийн мэдээлэл</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      {[
                        { key: "lastName", label: "Овог", placeholder: "БАТБАЯР" },
                        { key: "firstName", label: "Нэр", placeholder: "БОЛД" },
                        { key: "birthDate", label: "Төрсөн огноо", placeholder: "1995-06-15", type: "date" },
                        { key: "phone", label: "Утасны дугаар", placeholder: "+976 9911-2233" },
                        { key: "email", label: "И-мэйл", placeholder: "example@gmail.com", type: "email" },
                        { key: "plannedDepartureDate", label: "Явах огноо (тооцоолсон)", placeholder: "", type: "date" },
                      ].map(({ key, label, placeholder, type }) => (
                        <div key={key}>
                          <label className="block text-gray-400 text-sm mb-1">{label}</label>
                          <input
                            type={type || "text"}
                            placeholder={placeholder}
                            value={(formData as any)[key]}
                            onChange={(e) => setFormData((p) => ({ ...p, [key]: e.target.value }))}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                          />
                        </div>
                      ))}
                    </div>

                    {/* Optional passport fields with note */}
                    <div className="mb-6 p-4 rounded-xl border border-yellow-500/20 bg-yellow-500/5">
                      <div className="flex items-start gap-2 mb-3">
                        <span className="text-yellow-400 text-lg leading-none mt-0.5">⚠️</span>
                        <p className="text-yellow-300/80 text-sm">
                          <span className="font-semibold text-yellow-300">Гадаад пасспортын мэдээлэл заавал биш биш: </span>
                          Хэрэв бүртгээгүй бол, дараа оруулах болно. Манай зөвлөх хүн танд хүсэлтэй холбогдоно.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-gray-400 text-sm mb-1">
                            Паспортын дугаар <span className="text-gray-500 font-normal">(заавал биш)</span>
                          </label>
                          <input
                            type="text"
                            placeholder="AB123456"
                            value={formData.passportNumber}
                            onChange={(e) => setFormData((p) => ({ ...p, passportNumber: e.target.value }))}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-400 text-sm mb-1">
                            Паспорт дуусах огноо <span className="text-gray-500 font-normal">(заавал биш)</span>
                          </label>
                          <input
                            type="date"
                            value={formData.passportExpiry}
                            onChange={(e) => setFormData((p) => ({ ...p, passportExpiry: e.target.value }))}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                          />
                        </div>
                      </div>
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 text-red-400 bg-red-400/10 rounded-xl px-4 py-3 mb-4">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm">{error}</span>
                      </div>
                    )}

                    <div className="flex justify-between">
                      <button
                        onClick={() => setStep(1)}
                        className="flex items-center gap-2 text-gray-400 hover:text-white px-4 py-3 rounded-xl transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" /> Буцах
                      </button>
                      <button
                        disabled={
                          !formData.firstName ||
                          !formData.lastName ||
                          !formData.birthDate ||
                          !formData.phone ||
                          !formData.email ||
                          createApplication.isPending
                        }
                        onClick={handleSubmitApplication}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                      >
                        {createApplication.isPending ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Хадгалж байна...</>
                        ) : (
                          <>Дараагийн алхам <ChevronRight className="w-4 h-4" /></>
                        )}
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* STEP 3: Documents */}
                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h2 className="text-xl font-bold text-white mb-2">Баримт бичиг upload хийх</h2>
                    <p className="text-gray-400 text-sm mb-6">
                      Захиалгын дугаар: <span className="text-blue-400 font-bold">#{applicationId}</span> — Баримт бичгүүдийг upload хийнэ үү
                    </p>

                    <div className="space-y-3 mb-6">
                      {requiredDocs.map((doc) => {
                        const uploaded = uploadedDocs[doc.type];
                        const isUploading = uploadingDoc === doc.type;
                        return (
                          <div
                            key={doc.type}
                            className={`flex items-center justify-between p-4 rounded-xl border ${
                              uploaded
                                ? "border-green-500/50 bg-green-500/10"
                                : "border-white/10 bg-white/5"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {uploaded ? (
                                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                              ) : (
                                <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                              )}
                              <div>
                                <div className="text-white text-sm font-medium">
                                  {doc.label}
                                  {doc.required && <span className="text-red-400 ml-1">*</span>}
                                </div>
                                {uploaded && (
                                  <div className="text-green-400 text-xs">{uploaded.name}</div>
                                )}
                              </div>
                            </div>
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleDocUpload(doc.type, doc.label, file);
                                }}
                              />
                              <span
                                className={`flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg transition-colors ${
                                  isUploading
                                    ? "bg-white/10 text-gray-400 cursor-wait"
                                    : uploaded
                                    ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                                    : "bg-blue-600/20 text-blue-400 hover:bg-blue-600/30"
                                }`}
                              >
                                {isUploading ? (
                                  <><Loader2 className="w-3 h-3 animate-spin" /> Байна...</>
                                ) : uploaded ? (
                                  <><Upload className="w-3 h-3" /> Солих</>
                                ) : (
                                  <><Upload className="w-3 h-3" /> Upload</>
                                )}
                              </span>
                            </label>
                          </div>
                        );
                      })}
                    </div>

                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mb-6">
                      <p className="text-blue-300 text-sm">
                        💡 Одоо бүх баримт бичиг байхгүй бол дараа нь нэмж болно. Захиалга хадгалагдсан.
                      </p>
                    </div>

                    <div className="flex justify-between">
                      <button
                        onClick={() => setStep(2)}
                        className="flex items-center gap-2 text-gray-400 hover:text-white px-4 py-3 rounded-xl transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" /> Буцах
                      </button>
                      <button
                        onClick={() => setStep(4)}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                      >
                        Дараагийн алхам <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* STEP 4: Confirmation */}
                {step === 4 && (
                  <motion.div
                    key="step4"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h2 className="text-xl font-bold text-white mb-6">Захиалгын хураангуй</h2>

                    <div className="space-y-4 mb-6">
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <h3 className="text-gray-400 text-xs uppercase tracking-wider mb-3">Визийн мэдээлэл</h3>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <span className="text-gray-400">Улс:</span>
                          <span className="text-white font-medium">{selectedCountry?.name}</span>
                          <span className="text-gray-400">Визийн төрөл:</span>
                          <span className="text-white font-medium">{selectedVisa?.label}</span>
                          <span className="text-gray-400">Үйлчилгээний хөлс:</span>
                          <span className="text-blue-400 font-bold">{selectedVisa?.fee}</span>
                        </div>
                      </div>

                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <h3 className="text-gray-400 text-xs uppercase tracking-wider mb-3">Хувийн мэдээлэл</h3>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <span className="text-gray-400">Нэр:</span>
                          <span className="text-white font-medium">{formData.lastName} {formData.firstName}</span>
                          <span className="text-gray-400">Паспорт:</span>
                          <span className="text-white font-medium">{formData.passportNumber}</span>
                          <span className="text-gray-400">Утас:</span>
                          <span className="text-white font-medium">{formData.phone}</span>
                          <span className="text-gray-400">И-мэйл:</span>
                          <span className="text-white font-medium">{formData.email}</span>
                        </div>
                      </div>

                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <h3 className="text-gray-400 text-xs uppercase tracking-wider mb-3">
                          Баримт бичиг ({Object.keys(uploadedDocs).length}/{requiredDocs.filter(d => d.required).length} заавал)
                        </h3>
                        <div className="space-y-1">
                          {requiredDocs.map((doc) => (
                            <div key={doc.type} className="flex items-center gap-2 text-sm">
                              {uploadedDocs[doc.type] ? (
                                <CheckCircle2 className="w-4 h-4 text-green-400" />
                              ) : (
                                <div className="w-4 h-4 rounded-full border border-gray-600" />
                              )}
                              <span className={uploadedDocs[doc.type] ? "text-green-400" : "text-gray-400"}>
                                {doc.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6">
                      <p className="text-amber-300 text-sm">
                        ⚠️ Захиалга илгээсний дараа манай зөвлөх 24 цагийн дотор таны утасны дугаарт холбогдох болно.
                      </p>
                    </div>

                    <div className="flex justify-between">
                      <button
                        onClick={() => setStep(3)}
                        className="flex items-center gap-2 text-gray-400 hover:text-white px-4 py-3 rounded-xl transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" /> Буцах
                      </button>
                      <button
                        onClick={handleFinish}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Захиалга баталгаажуулах
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
